import {
  formatCompactCurrency,
  formatCount,
  formatCurrency,
} from "../components/metrics/metricsFormat";
import { barWidthPercent, maxPositive } from "../pages/returns-analysis/returnsAnalysisUi";

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const utcStamp = (iso) => {
  const date = iso ? new Date(iso) : new Date();
  const instant = Number.isNaN(date.getTime()) ? new Date() : date;
  return instant
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}Z$/, "Z");
};

const fileSafeDate = (value) => {
  const text = String(value || "").trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : "unknown";
};

export const buildReturnsAnalysisExportFileName = (payload, generatedAt) => {
  const start = fileSafeDate(payload?.start_date);
  const end = fileSafeDate(payload?.end_date);
  return `returns-analysis_${start}_to_${end}_generated-${utcStamp(generatedAt)}.html`;
};

const channelList = (payload) =>
  (payload?.channels || []).filter((value) => value != null && value !== "");

const channelLabel = (payload) => {
  const channels = channelList(payload);
  if (channels.length === 0) return "All Channels";
  if (channels.length === 1) return channels[0];
  return `${channels.length} channels: ${channels.join(", ")}`;
};

const formatPct = (value) => (value == null ? "—" : `${value}%`);

const formatReturnRate = (rate) =>
  rate == null ? "—" : `${(Number(rate) * 100).toFixed(1)}%`;

const niceMax = (value) => {
  const numeric = Number(value) || 0;
  if (numeric <= 0) return 1;
  const exp = 10 ** Math.floor(Math.log10(numeric));
  const n = numeric / exp;
  const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return nice * exp;
};

/** YYYY-MM-DD -> UTC Date, or null when the value isn't a plain date-only key. */
const parseDateKey = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return null;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "Aug 12" — compact axis tick, no year (report-level meta already carries the year). */
const axisDateLabel = (value) => {
  const date = parseDateKey(value);
  if (!date) return String(value || "").slice(5);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
};

/** "Aug 12, 2026" — full display date for tooltips/report meta. */
const fullDateLabel = (value) => {
  const date = parseDateKey(value);
  if (!date) return value ? String(value) : "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
};

/** "Aug 1 – Sep 10, 2026" (or "Aug 1, 2025 – Sep 10, 2026" across a year boundary). */
const dateRangeLabel = (start, end) => {
  const startDate = parseDateKey(start);
  const endDate = parseDateKey(end);
  if (!startDate || !endDate) {
    return `${start || "—"} – ${end || "—"}`;
  }
  const sameYear = startDate.getUTCFullYear() === endDate.getUTCFullYear();
  const startLabel = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
    timeZone: "UTC",
  }).format(startDate);
  return `${startLabel} – ${fullDateLabel(end)}`;
};

/** "Sep 10, 2026, 3:45 PM UTC" — absolute, timezone-explicit so a shared file is unambiguous. */
const generatedLabel = (iso) => {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return iso ? String(iso) : "—";
  const formatted = new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
  }).format(date);
  return `${formatted} UTC`;
};

const kpiCard = (label, value, sub) => `
  <div class="kpi">
    <span class="kpi-label">${escapeHtml(label)}</span>
    <strong class="kpi-value">${escapeHtml(value)}</strong>
    ${sub ? `<em class="kpi-sub">${escapeHtml(sub)}</em>` : ""}
  </div>
`;

const statusBar = (label, colorClass, units, value, maxUnits) => {
  const width = barWidthPercent(units, maxUnits);
  return `
    <div class="bar-row">
      <div class="bar-head">
        <span class="legend"><i class="${escapeHtml(colorClass)}"></i>${escapeHtml(label)}</span>
        <strong>${escapeHtml(formatCount(units))} units · ${escapeHtml(formatCurrency(value))}</strong>
      </div>
      <div class="track"><div class="fill ${escapeHtml(colorClass)}" style="width:${width}%"></div></div>
    </div>
  `;
};

const buildOverTimeChart = (byDate = []) => {
  if (!byDate.length) {
    return `<p class="empty">No daily returns in this period.</p>`;
  }

  const width = 960;
  const height = 300;
  const pad = { l: 56, r: 52, t: 16, b: 34 };
  const innerW = width - pad.l - pad.r;
  const innerH = height - pad.t - pad.b;
  const n = byDate.length;
  const slot = innerW / n;
  const valueTop = niceMax(maxPositive(byDate.map((row) => row.return_value)));
  const unitsTop = niceMax(maxPositive(byDate.map((row) => row.return_units)));
  const xCenter = (i) => pad.l + (i + 0.5) * slot;
  const yValue = (value) => pad.t + innerH - ((Number(value) || 0) / valueTop) * innerH;
  const barW = Math.min(slot * 0.55, 22);
  // Intelligently thin x-axis labels as the range grows so dates never overlap.
  const labelEvery = n <= 10 ? 1 : n <= 20 ? 2 : n <= 40 ? 4 : Math.ceil(n / 10);

  const points = byDate.map((row, i) => ({
    x: xCenter(i),
    y: yValue(row.return_value),
  }));
  const line = points
    .map((point, i) => `${i === 0 ? "M" : "L"} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`)
    .join(" ");
  const area = `${line} L ${points[points.length - 1].x.toFixed(1)} ${(pad.t + innerH).toFixed(1)} L ${points[0].x.toFixed(1)} ${(pad.t + innerH).toFixed(1)} Z`;

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => {
    const y = pad.t + innerH * (1 - ratio);
    return `
      <line class="grid" x1="${pad.l}" x2="${pad.l + innerW}" y1="${y.toFixed(1)}" y2="${y.toFixed(1)}" />
      <text class="axis left" x="${pad.l - 10}" y="${y.toFixed(1)}" dy="0.35em">${escapeHtml(formatCompactCurrency(valueTop * ratio))}</text>
      <text class="axis right" x="${pad.l + innerW + 10}" y="${y.toFixed(1)}" dy="0.35em">${escapeHtml(formatCount(Math.round(unitsTop * ratio)))}</text>
    `;
  });

  const bars = byDate
    .map((row, i) => {
      const units = Number(row.return_units) || 0;
      const h = (units / unitsTop) * innerH;
      if (h <= 0) return "";
      const x = xCenter(i) - barW / 2;
      const y = pad.t + innerH - h;
      return `<rect class="units-bar" x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${barW.toFixed(1)}" height="${h.toFixed(1)}" rx="2" />`;
    })
    .join("");

  const xLabels = byDate
    .map((row, i) => {
      if (i % labelEvery !== 0 && i !== n - 1) return "";
      return `<text class="axis x" x="${xCenter(i).toFixed(1)}" y="${height - 10}">${escapeHtml(axisDateLabel(row.date))}</text>`;
    })
    .join("");

  const hits = byDate
    .map((row, i) => {
      const tip = `${fullDateLabel(row.date)}\nReturn Value ${formatCurrency(row.return_value)} · Return Units ${formatCount(row.return_units)}`;
      const title = `${fullDateLabel(row.date)}: Return Value ${formatCurrency(row.return_value)}, Return Units ${formatCount(row.return_units)}`;
      return `
        <rect class="hit" x="${(pad.l + i * slot).toFixed(1)}" y="${pad.t}" width="${slot.toFixed(1)}" height="${innerH}" data-tip="${escapeHtml(tip)}">
          <title>${escapeHtml(title)}</title>
        </rect>`;
    })
    .join("");

  return `
    <div class="chart-wrap" id="over-time-chart">
      <svg viewBox="0 0 ${width} ${height}" role="img" aria-label="Returns over time">
        ${yTicks.join("")}
        <path class="value-area" d="${area}" />
        <path class="value-line" d="${line}" />
        ${bars}
        <line class="baseline" x1="${pad.l}" x2="${pad.l + innerW}" y1="${pad.t + innerH}" y2="${pad.t + innerH}" />
        ${xLabels}
        ${hits}
      </svg>
      <div class="chart-tip" id="over-time-tooltip" hidden></div>
    </div>
  `;
};

const buildChannelTable = (rows = []) => {
  if (!rows.length) {
    return `<p class="empty">No returns in this period.</p>`;
  }
  const maxUnits = maxPositive(rows.map((row) => row.return_units));
  return `
    <table class="channel-table">
      <thead>
        <tr>
          <th>Channel</th>
          <th class="bar-col"></th>
          <th class="num">Return Units</th>
          <th class="num">Return Value</th>
          <th class="num">Units Sold</th>
          <th class="num">Return Rate</th>
        </tr>
      </thead>
      <tbody>
        ${rows
          .map(
            (row) => `
          <tr>
            <td>${escapeHtml(row.channel)}</td>
            <td><div class="track"><div class="fill accent" style="width:${barWidthPercent(row.return_units, maxUnits)}%"></div></div></td>
            <td class="num">${escapeHtml(formatCount(row.return_units))}</td>
            <td class="num">${escapeHtml(formatCurrency(row.return_value))}</td>
            <td class="num">${escapeHtml(row.units_sold == null ? "—" : formatCount(row.units_sold))}</td>
            <td class="num">${escapeHtml(formatReturnRate(row.return_rate))}</td>
          </tr>`,
          )
          .join("")}
      </tbody>
    </table>
  `;
};

const buildAgeBuckets = (age = {}) => {
  const buckets = age.buckets || [];
  if (!buckets.length || buckets.every((bucket) => (bucket.count || 0) === 0)) {
    return `<p class="empty">No open returns in this period.</p>`;
  }
  const maxUnits = maxPositive(buckets.map((bucket) => bucket.units));
  return `
    <div class="age-grid">
      ${buckets
        .map(
          (bucket) => `
        <div class="age-bucket">
          <span class="age-label">${escapeHtml(bucket.bucket)} days</span>
          <div class="track"><div class="fill accent" style="width:${barWidthPercent(bucket.units, maxUnits)}%"></div></div>
          <strong>${escapeHtml(formatCount(bucket.units))} units</strong>
          <em>${escapeHtml(formatCount(bucket.count))} returns</em>
        </div>`,
        )
        .join("")}
    </div>
  `;
};

const buildSkuTable = (rows = []) => {
  if (!rows.length) {
    return `<p class="empty">No SKU activity in this period.</p>`;
  }
  return `
    <table class="sku-table">
      <thead>
        <tr>
          <th>SKU</th>
          <th class="num">Units Sold</th>
          <th class="num">Returned Units</th>
          <th class="num">Return Rate</th>
        </tr>
      </thead>
      <tbody>
        ${rows
          .map(
            (row) => `
          <tr>
            <td>${escapeHtml(row.sku)}</td>
            <td class="num">${escapeHtml(formatCount(row.units_sold))}</td>
            <td class="num">${escapeHtml(formatCount(row.returned_units))}</td>
            <td class="num">${escapeHtml(formatReturnRate(row.return_rate))}</td>
          </tr>`,
          )
          .join("")}
      </tbody>
    </table>
  `;
};

const skuSummaryLabel = (rows = []) =>
  rows.length ? `${formatCount(rows.length)} SKU${rows.length === 1 ? "" : "s"} with sales or returns in this period` : "";

export const buildReturnsAnalysisHtml = (payload = {}, { clientName, generatedAt } = {}) => {
  const totals = payload.totals || {};
  const status = payload.status || {};
  const inProgress = status.in_progress || {};
  const complete = status.complete || {};
  const maxStatusUnits = maxPositive([inProgress.units, complete.units]);
  const generated = generatedAt || new Date().toISOString();
  const generatedText = generatedLabel(generated);
  const periodText = dateRangeLabel(payload.start_date, payload.end_date);
  const channelText = channelLabel(payload);
  const skuRows = payload.sku || [];

  const clientNameSafe = clientName ? escapeHtml(clientName) : "";
  const clientIdSafe = payload.client_id ? escapeHtml(payload.client_id) : "";
  const clientIdBadge = clientIdSafe ? `<span class="client-id">Client ID: ${clientIdSafe}</span>` : "";
  const clientLine = clientNameSafe
    ? `<p class="client-name">${clientNameSafe}${clientIdBadge ? ` ${clientIdBadge}` : ""}</p>`
    : clientIdBadge
      ? `<p class="client-name">${clientIdBadge}</p>`
      : "";
  const footerClientText = clientName ? `${clientName}${payload.client_id ? ` (${payload.client_id})` : ""}` : payload.client_id || "All clients";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Returns Analysis${clientName ? ` — ${clientName}` : ""}</title>
  <style>
    :root { color-scheme: light; }
    * { box-sizing: border-box; }
    body {
      font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
      margin: 0;
      color: #1f2933;
      background: #eef1f5;
      -webkit-font-smoothing: antialiased;
    }
    main { max-width: 1120px; margin: 0 auto; padding: 36px 28px 56px; }
    h1 { margin: 0; font-size: 25px; font-weight: 700; letter-spacing: -0.01em; }
    h2 { margin: 0; font-size: 15px; font-weight: 700; color: #1f2933; }
    p { margin: 0; }
    em { font-style: normal; }

    /* ---------- Report header ---------- */
    .report-header {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      align-items: flex-end;
      gap: 20px;
      background: #fff;
      border: 1px solid #d9e2ec;
      border-radius: 14px;
      padding: 22px 26px;
      margin-bottom: 22px;
      box-shadow: 0 1px 2px rgba(16,24,40,0.04);
    }
    .eyebrow {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #2e5fe8;
      margin: 0 0 4px;
    }
    .client-name { margin-top: 6px; font-size: 15px; font-weight: 600; color: #334155; }
    .client-id { margin-left: 6px; font-size: 12px; font-weight: 500; color: #8a97a8; }
    .report-meta { display: flex; flex-wrap: wrap; gap: 22px 28px; margin: 0; }
    .report-meta dt { font-size: 10.5px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #8a97a8; margin: 0 0 3px; }
    .report-meta dd { margin: 0; font-size: 13.5px; font-weight: 600; color: #1f2933; }

    /* ---------- KPI cards ---------- */
    .kpis { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin: 0 0 22px; }
    .kpi {
      background: #fff;
      border: 1px solid #d9e2ec;
      border-radius: 14px;
      box-shadow: 0 1px 2px rgba(16,24,40,0.04);
      padding: 18px 20px 16px;
      min-height: 116px;
      display: flex;
      flex-direction: column;
    }
    .kpi-label { font-size: 11.5px; font-weight: 700; letter-spacing: 0.03em; color: #64748b; }
    .kpi-value { display: block; margin-top: 10px; font-size: 28px; font-weight: 700; letter-spacing: -0.015em; font-variant-numeric: tabular-nums; color: #111827; }
    .kpi-sub { display: block; margin-top: auto; padding-top: 12px; border-top: 1px solid #eef1f4; font-size: 12px; color: #64748b; }

    /* ---------- Section cards ---------- */
    section.card {
      background: #fff;
      border: 1px solid #d9e2ec;
      border-radius: 14px;
      box-shadow: 0 1px 2px rgba(16,24,40,0.04);
      padding: 20px 22px 22px;
      margin-bottom: 18px;
    }
    .head {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 16px;
      padding-bottom: 12px;
      margin-bottom: 16px;
      border-bottom: 1px solid #eef1f4;
    }
    .sub { display: block; margin-top: 4px; font-size: 12.5px; color: #64748b; line-height: 1.45; }
    .sku-count { margin-top: 2px; font-weight: 600; color: #52606d; }

    /* ---------- Legends / bars ---------- */
    .legend { display: inline-flex; align-items: center; gap: 6px; }
    .legend i { display: inline-block; width: 8px; height: 8px; border-radius: 99px; }
    .legends { display: flex; gap: 8px; flex-shrink: 0; }
    .legends .legend { background: #f5f6f8; border-radius: 99px; padding: 4px 10px; font-size: 11.5px; color: #52606d; font-weight: 600; }
    .bar-row { margin-bottom: 14px; }
    .bar-row:last-child { margin-bottom: 0; }
    .bar-head { display: flex; justify-content: space-between; gap: 12px; margin-bottom: 7px; font-size: 13px; }
    .bar-head strong { font-variant-numeric: tabular-nums; }
    .track { height: 9px; border-radius: 5px; background: #eef1f4; overflow: hidden; }
    .fill { height: 100%; border-radius: 5px; }
    .fill.warning, i.warning { background: #9a5b00; }
    .fill.positive, i.positive { background: #1c7a4e; }
    .fill.accent, i.accent { background: #2e5fe8; }
    i.units { background: #7c3aed; }

    /* ---------- Tables ---------- */
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th, td { padding: 10px 8px; text-align: left; border-bottom: 1px solid #eef1f4; }
    th { font-size: 10.5px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #8a97a8; }
    td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
    .bar-col { width: 34%; }
    .channel-table tbody tr:last-child td, .sku-table tbody tr:last-child td { border-bottom: none; }
    .sku-table tbody tr:nth-child(even) { background: #f8fafc; }
    .sku-table td, .sku-table th { padding: 8px 8px; }

    /* ---------- Open Return Age ---------- */
    .age-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
    .age-bucket { background: #f8fafc; border: 1px solid #eef1f4; border-radius: 10px; padding: 12px 14px; }
    .age-label { display: block; font-size: 11.5px; font-weight: 700; color: #64748b; margin-bottom: 8px; }
    .age-bucket .track { margin-bottom: 8px; }
    .age-bucket strong { display: block; font-size: 14px; font-variant-numeric: tabular-nums; }
    .age-bucket em { display: block; margin-top: 2px; font-size: 11.5px; color: #64748b; }
    .age-highlight { font-size: 13px; font-weight: 700; color: #1f2933; white-space: nowrap; }

    /* ---------- Returns Over Time chart ---------- */
    .chart-wrap { position: relative; margin-top: 4px; }
    svg { width: 100%; height: auto; display: block; }
    .grid { stroke: #eef1f4; stroke-dasharray: 3 3; }
    .baseline { stroke: #cbd5e1; }
    .axis { fill: #94a3b8; font-size: 10.5px; }
    .axis.left { text-anchor: end; }
    .axis.right { text-anchor: start; }
    .axis.x { text-anchor: middle; }
    .value-area { fill: #2e5fe8; fill-opacity: 0.12; }
    .value-line { fill: none; stroke: #2e5fe8; stroke-width: 2; }
    .units-bar { fill: #7c3aed; fill-opacity: 0.85; }
    .hit { fill: transparent; cursor: default; }
    .chart-tip {
      position: absolute;
      z-index: 2;
      max-width: 260px;
      padding: 9px 11px;
      border: 1px solid #d9e2ec;
      border-radius: 8px;
      background: #fff;
      color: #1f2933;
      font-size: 12px;
      line-height: 1.5;
      white-space: pre-line;
      box-shadow: 0 4px 14px rgba(16,24,40,0.12);
      pointer-events: none;
    }
    .chart-tip:first-line { font-weight: 700; }

    /* ---------- Footer ---------- */
    .report-footer {
      margin-top: 26px;
      padding-top: 18px;
      border-top: 1px solid #d9e2ec;
      font-size: 12px;
      color: #8a97a8;
      line-height: 1.7;
    }
    .report-footer .footer-title { font-weight: 700; color: #52606d; }
    .empty { color: #8a97a8; font-size: 13px; padding: 4px 0; }

    @media (max-width: 860px) {
      .kpis, .age-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .report-header { align-items: flex-start; }
    }
    @media (max-width: 560px) {
      main { padding: 20px 14px 40px; }
      .kpis, .age-grid { grid-template-columns: 1fr; }
      .report-meta { gap: 14px 20px; }
      .head { flex-direction: column; }
    }

    /* ---------- Print / Save as PDF ---------- */
    @media print {
      @page { margin: 14mm 12mm; }
      body { background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      main { max-width: none; padding: 0; }
      .report-header, .kpis, .kpi, section.card { box-shadow: none; }
      .report-header, .kpis { break-inside: avoid; }
      section.card { break-inside: avoid; }
      section.card.table-card { break-inside: auto; }
      section.card.table-card > .head { break-after: avoid; }
      table thead { display: table-header-group; }
      table tr { break-inside: avoid; }
      .chart-tip { display: none !important; }
      .report-footer { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <main>
    <header class="report-header">
      <div class="report-title">
        <p class="eyebrow">Analytics Report</p>
        <h1>Returns Analysis</h1>
        ${clientLine}
      </div>
      <dl class="report-meta">
        <div><dt>Reporting period</dt><dd>${escapeHtml(periodText)}</dd></div>
        <div><dt>Channel</dt><dd>${escapeHtml(channelText)}</dd></div>
        <div><dt>Generated</dt><dd>${escapeHtml(generatedText)}</dd></div>
      </dl>
    </header>

    <div class="kpis">
      ${kpiCard("Total Return Units", formatCount(totals.return_units), periodText)}
      ${kpiCard("Return Value ($)", formatCurrency(totals.return_value), "Merchandise subtotal, not a cash-refund ledger")}
      ${kpiCard("Open Return Units", formatCount(totals.open_return_units), `${formatCount(totals.open_return_count)} open returns`)}
      ${kpiCard("% Open Returns < 30 Days", formatPct(totals.pct_open_under_30), "Of currently open (in-progress) returns")}
    </div>

    <section class="card">
      <div class="head">
        <div>
          <h2>Status</h2>
          <p class="sub">In Progress (Draft / Expected / Received / Refunded / Shipped / Authorized / Pending) vs. Complete (Completed / Closed / Returned). Cancelled and Expired returns are excluded.</p>
        </div>
      </div>
      ${statusBar("In Progress", "warning", inProgress.units, inProgress.value, maxStatusUnits)}
      ${statusBar("Complete", "positive", complete.units, complete.value, maxStatusUnits)}
    </section>

    <section class="card table-card">
      <div class="head">
        <div>
          <h2>Returns by Channel</h2>
          <p class="sub">${escapeHtml(channelText)} · Return rate = returned units requested in this period ÷ units sold in this period (not a sale-cohort rate)</p>
        </div>
      </div>
      ${buildChannelTable(payload.by_channel || [])}
    </section>

    <section class="card">
      <div class="head">
        <div>
          <h2>Returns Over Time</h2>
          <p class="sub">By requested date</p>
        </div>
        <div class="legends">
          <span class="legend"><i class="accent"></i>Return Value ($)</span>
          <span class="legend"><i class="units"></i>Return Units</span>
        </div>
      </div>
      ${buildOverTimeChart(payload.by_date || [])}
    </section>

    <section class="card">
      <div class="head">
        <div>
          <h2>Open Return Age</h2>
          <p class="sub">Calendar days since requested_at, for currently open (in-progress) returns only</p>
        </div>
        <span class="age-highlight">${escapeHtml(formatPct(payload.age?.pct_under_30))} under 30 days</span>
      </div>
      ${buildAgeBuckets(payload.age)}
    </section>

    <section class="card table-card">
      <div class="head">
        <div>
          <h2>SKU Return Rate</h2>
          <p class="sub">Returned units requested in this period ÷ units sold in this period (not a sale-cohort rate)</p>
          ${skuSummaryLabel(skuRows) ? `<p class="sub sku-count">${escapeHtml(skuSummaryLabel(skuRows))}</p>` : ""}
        </div>
      </div>
      ${buildSkuTable(skuRows)}
    </section>

    <footer class="report-footer">
      <p class="footer-title">Returns Analysis · ${escapeHtml(footerClientText)}</p>
      <p>Reporting period: ${escapeHtml(periodText)} · Generated: ${escapeHtml(generatedText)}</p>
      ${payload.meta?.return_value_basis ? `<p>${escapeHtml(payload.meta.return_value_basis)}</p>` : ""}
    </footer>
  </main>
  <script>
    (function () {
      var chart = document.getElementById("over-time-chart");
      var tip = document.getElementById("over-time-tooltip");
      if (!chart || !tip) return;
      chart.querySelectorAll("[data-tip]").forEach(function (el) {
        el.addEventListener("mouseenter", function () {
          tip.textContent = el.getAttribute("data-tip") || "";
          tip.hidden = false;
        });
        el.addEventListener("mousemove", function (event) {
          var box = chart.getBoundingClientRect();
          var left = event.clientX - box.left + 12;
          var top = event.clientY - box.top + 12;
          if (left + 200 > box.width) left = event.clientX - box.left - 188;
          tip.style.left = left + "px";
          tip.style.top = top + "px";
        });
        el.addEventListener("mouseleave", function () {
          tip.hidden = true;
        });
      });
    })();
  </script>
</body>
</html>`;
};

export const downloadReturnsAnalysisHtml = (payload, options = {}) => {
  if (!payload) return;
  const generatedAt = options.generatedAt || new Date().toISOString();
  const html = buildReturnsAnalysisHtml(payload, { ...options, generatedAt });
  const fileName =
    options.fileName || buildReturnsAnalysisExportFileName(payload, generatedAt);
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
